import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canAccess } from '../utils/rolePermissions';

/**
 * ProtectedRoute - Wraps routes that require specific permissions
 * 
 * @param {Object} props
 * @param {React.Component} props.children - The component to render if authorized
 * @param {string} props.resource - The resource name from RESOURCES (e.g., RESOURCES.DASHBOARD)
 * @param {Array<string>} props.allowedRoles - (Optional) Array of roles that can access this route
 * @param {Array<string>} props.blockedRoles - (Optional) Array of roles that cannot access this route
 * 
 * Usage examples:
 * // Using resource-based access (recommended)
 * <ProtectedRoute resource={RESOURCES.DASHBOARD}>
 *   <Dashboard />
 * </ProtectedRoute>
 * 
 * // Using blocked roles
 * <ProtectedRoute blockedRoles={[ROLES.OPERATIONS]}>
 *   <Dashboard />
 * </ProtectedRoute>
 * 
 * // Using allowed roles
 * <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.MANAGER]}>
 *   <Users />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({ children, resource, allowedRoles, blockedRoles }) => {
  const { user } = useAuth();
  const userRole = user?.role;

  // If resource is specified, use RBAC system
  if (resource) {
    if (!canAccess(userRole, resource)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // If allowedRoles is specified, check if user role is in the list
  if (allowedRoles && allowedRoles.length > 0) {
    if (!userRole || !allowedRoles.includes(userRole)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // If blockedRoles is specified, check if user role is NOT in the list
  if (blockedRoles && blockedRoles.length > 0) {
    if (userRole && blockedRoles.includes(userRole)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // User is authorized, render the children
  return children;
};

export default ProtectedRoute;

